import Decimal from 'decimal.js';
import * as yup from 'yup';
import {
  DisabilityInsurancePolicyType,
  HealthPlanTypeEnum,
  InsuranceAccountDTO,
  InsuranceCoverageType,
  InsurancePeriodType,
  InsuranceType,
  LifeInsurancePermanentTypeEnum,
  LifeInsurancePolicyTypeEnum,
  OwnerModelDTO,
  OwnerTypeDTO,
  PropertyAndCasualtyDTO,
  PropertyPremiumFrequencyDTO,
} from '../../api/useInsuranceQuery';
import {
  AccountBeneficiariesDTO,
  TaxStatusDTO,
} from '../../../../Profile/Accounts/api/useAccountsQuery';
import { InsuranceToUpsert } from '../../Insurance.meta';
import { getColorByColorGroup } from '../../../../CommonUtils/getAssetColors';
import { AccountPortfolioDTO } from '../../../../Profile/Accounts/api/useAccountPortfolioQuery';
import { AccountType } from '../../../../Profile/Accounts/api/useAccountsOptionsQuery';
import { AssetsDTO } from '../../../../CommonTypes/AssetsDTO';
import { InsuranceData } from '../../useInsurance';
import { hypotheticalReturnValidation } from '../../../../CommonValidations/hypoteticalReturn';
import { AssetAllocationDTO } from '../../../../Profile/Accounts/api/useDefaultAssetAllocationQuery';
import {
  beneficiaryRowMatchesOwner,
  getBeneficiaryOptionRowsForOwner,
  getDefaultAssetAllocation,
} from '../../../../Profile/Accounts/helpers';
import { ManualAccountsAssetAllocationTaxStatusIdDTO } from '../../../../ManualAccountsAssetAllocation/types';
import { BeneficiaryType } from '../../../PersonalInfo/api/useTrustsQuery';

export const EXCLUDE_OPTION = {
  label: 'Exclude from Asset Bucketing',
  value: -1,
};

export const UPSERT_INSURANCE_FORM_ID = 'insurance--upsert-insurance--form';

interface PropertyAndCasualtyField
  extends Omit<PropertyAndCasualtyDTO, 'CoverageStartYear' | 'Coverage'> {
  CoverageStartYear: number | null;
  Coverage: number | null;
}

interface InsuranceAccountFormField {
  AccountName: string;
  AccountOwner: string;
  AccountBeneficiaries: AccountBeneficiariesDTO;
  DisabilityMonthlyBenefitAmount: number | null;
  IsBrokerOfRecord: boolean;
  InstitutionName: string;
  InstitutionId: string | null;
  InsuranceType?: InsuranceType;
  InsuranceCoverageType: InsuranceCoverageType | null;
  DisabilityInsurancePolicyType: DisabilityInsurancePolicyType | null;
  Premium: number | null;
  IsPreTaxPremium: boolean;
  DisabilityCoveragePeriod: { Value: number | null; PeriodType: InsurancePeriodType } | null;
  DisabilityEliminationPeriod: { Value: number | null; PeriodType: InsurancePeriodType } | null;
  LongTermCareDailyBenefitAmount: number | null;
  LongTermCareHomeCareCoveragePercentage: number | null;
  LongTermCareNursingCareCoveragePercentage: number | null;
  LongTermCareCoveragePeriod: { Value: number | null; PeriodType: InsurancePeriodType } | null;
  LongTermCareEliminationPeriod: { Value: number | null; PeriodType: InsurancePeriodType } | null;
  InsurancePremiumEndYear: string | null;
  HealthInsuredMembers: string[] | null;
  HealthPlanType: HealthPlanTypeEnum | null;
  HealthIndividualDeductible: number | null;
  HealthFamilyDeductible: number | null;
  HealthCoinsurance: number | null;
  HealthCopayment: number | null;
  HealthOutOfPocketLimit: number | null;
  LifeInsurancePolicyType: LifeInsurancePolicyTypeEnum | null;
  LifeInsurancePolicyAmount: number | null;
  NonSpecificPositionsValue: number | null;
  CustomAssetBucketIndex: number | null;
  LifeInsurancePermanentType: LifeInsurancePermanentTypeEnum | null;
  LifeInsuranceGrowthRate: number | null;
  LifeInsurancePolicyTerm: string | null;
  LifeInsurancePolicyStartYear: number | null;
  LifeInsuredMember: string | null;
  IsLinked: boolean;
  IsIntegrated: boolean;
  ShowAccount: boolean;
  SyncAccountValueEnabled: boolean;
  IncludeInGrossEstate: boolean;
  PropertyAndCasualtyInsuranceDetails: PropertyAndCasualtyField | null;
  OrionManagementStyleId: number | null;
  IsOrion: boolean;
  HasAdvisorChangedBrokerOfRecord: boolean;
  OwnerModel: OwnerModelDTO[];
}

export interface FormFields extends InsuranceAccountFormField {
  accountPortfolio?: {
    assets: AssetsDTO[];
    hypotheticalReturn: string | number;
  };
}

export const dataToForm = (
  insurance: InsuranceAccountDTO | null,
  data: InsuranceData,
): InsuranceAccountFormField => {
  if (insurance?.AccountId) {
    const getDefaultCustomBucketIndex = () => {
      if (data.accountOptions.AssetBucketingByManagementStyle) {
        if (insurance.OrionManagementStyleId === null) {
          return null;
        }

        if (insurance.CustomAssetBucketIndex === null) {
          const selectedManagementStyle = (data.accountOptions.ManagementStyles || []).find(
            option => option.id === insurance.OrionManagementStyleId,
          );

          return selectedManagementStyle?.defaultBucket ?? EXCLUDE_OPTION.value;
        }
      }

      return insurance.CustomAssetBucketIndex;
    };

    return {
      ...insurance,
      AccountName: insurance.AccountName,
      AccountOwner: insurance.AccountOwner,
      AccountBeneficiaries: {
        Beneficiaries: [...(insurance.AccountBeneficiaries?.Beneficiaries || [])],
        LastBeneficiaryUpdate: insurance.AccountBeneficiaries?.LastBeneficiaryUpdate || null,
      },
      DisabilityMonthlyBenefitAmount: insurance.DisabilityMonthlyBenefitAmount,
      IsBrokerOfRecord: insurance.IsBrokerOfRecord || false,
      InstitutionName: insurance.InstitutionName,
      InstitutionId: insurance.InstitutionId,
      InsuranceType: insurance.InsuranceType,
      InsuranceCoverageType: insurance.InsuranceCoverageType,
      DisabilityInsurancePolicyType: insurance.DisabilityInsurancePolicyType,
      Premium: insurance.Premium,
      IsPreTaxPremium: insurance.IsPreTaxPremium,
      DisabilityCoveragePeriod: insurance.DisabilityCoveragePeriod
        ? {
            Value: insurance.DisabilityCoveragePeriod.Value,
            PeriodType: insurance.DisabilityCoveragePeriod.PeriodType,
          }
        : null,
      DisabilityEliminationPeriod: insurance.DisabilityEliminationPeriod
        ? {
            Value: insurance.DisabilityEliminationPeriod.Value,
            PeriodType: insurance.DisabilityEliminationPeriod.PeriodType,
          }
        : null,
      LongTermCareDailyBenefitAmount: insurance.LongTermCareDailyBenefitAmount,
      LongTermCareHomeCareCoveragePercentage: insurance.LongTermCareHomeCareCoveragePercentage,
      LongTermCareNursingCareCoveragePercentage:
        insurance.LongTermCareNursingCareCoveragePercentage,
      LongTermCareCoveragePeriod: insurance.LongTermCareCoveragePeriod,
      LongTermCareEliminationPeriod: insurance.LongTermCareEliminationPeriod,
      InsurancePremiumEndYear: insurance.InsurancePremiumEndYear,
      HealthInsuredMembers: insurance.HealthInsuredMembers,
      HealthPlanType: insurance.HealthPlanType,
      HealthIndividualDeductible: insurance.HealthIndividualDeductible,
      HealthFamilyDeductible: insurance.HealthFamilyDeductible,
      HealthCoinsurance: insurance.HealthCoinsurance,
      HealthCopayment: insurance.HealthCopayment,
      HealthOutOfPocketLimit: insurance.HealthOutOfPocketLimit,
      LifeInsurancePolicyType: insurance.LifeInsurancePolicyType,
      LifeInsurancePolicyAmount: insurance.LifeInsurancePolicyAmount,
      NonSpecificPositionsValue: insurance.NonSpecificPositionsValue,
      LifeInsurancePermanentType: insurance.LifeInsurancePermanentType,
      LifeInsuranceGrowthRate: insurance.LifeInsuranceGrowthRate,
      LifeInsurancePolicyTerm: insurance.LifeInsurancePolicyTerm,
      LifeInsurancePolicyStartYear: insurance.LifeInsurancePolicyStartYear,
      LifeInsuredMember: insurance.LifeInsuredMember,
      IsIntegrated: insurance.IsIntegrated,
      IsLinked: insurance.IsLinked,
      ShowAccount: insurance.ShowAccount,
      SyncAccountValueEnabled: insurance.SyncAccountValueEnabled,
      IncludeInGrossEstate: insurance.IncludeInGrossEstate,
      PropertyAndCasualtyInsuranceDetails: insurance.PropertyAndCasualtyInsuranceDetails,
      OrionManagementStyleId: insurance.OrionManagementStyleId,
      CustomAssetBucketIndex: getDefaultCustomBucketIndex(),
      OwnerModel: insurance.OwnerModel,
    };
  }

  return {
    AccountName: '',
    AccountOwner: data.Options.InsuranceOwnerOptions.People[0].Name,
    AccountBeneficiaries: {
      Beneficiaries: [],
      LastBeneficiaryUpdate: null,
    },
    DisabilityMonthlyBenefitAmount: null,
    IsBrokerOfRecord: false,
    InstitutionName: '',
    InstitutionId: null,
    InsuranceType: insurance?.InsuranceType,
    InsuranceCoverageType: null,
    DisabilityInsurancePolicyType: null,
    Premium: null,
    IsPreTaxPremium: false,
    DisabilityCoveragePeriod: null,
    DisabilityEliminationPeriod: null,
    LongTermCareDailyBenefitAmount: null,
    LongTermCareHomeCareCoveragePercentage: null,
    LongTermCareNursingCareCoveragePercentage: null,
    LongTermCareCoveragePeriod: null,
    LongTermCareEliminationPeriod: null,
    InsurancePremiumEndYear: null,
    HealthInsuredMembers: null,
    HealthPlanType: null,
    HealthIndividualDeductible: null,
    HealthFamilyDeductible: null,
    HealthCoinsurance: null,
    HealthCopayment: null,
    HealthOutOfPocketLimit: null,
    LifeInsurancePolicyType: null,
    LifeInsurancePolicyAmount: null,
    NonSpecificPositionsValue: null,
    CustomAssetBucketIndex: null,
    LifeInsurancePermanentType: null,
    LifeInsuranceGrowthRate: null,
    LifeInsurancePolicyTerm: null,
    LifeInsurancePolicyStartYear: null,
    LifeInsuredMember: null,
    IsIntegrated: false,
    IsLinked: false,
    ShowAccount: true,
    SyncAccountValueEnabled: false,
    PropertyAndCasualtyInsuranceDetails:
      insurance?.InsuranceType === InsuranceType.PropertyAndCasualty
        ? {
            PropertyTypes: [],
            CoverageStartYear: null,
            CoverageEndYear: null,
            Coverage: null,
            PremiumPaymentFrequency: PropertyPremiumFrequencyDTO.Annually,
            UseInflation: false,
            Comment: '',
            CasualtyCoverageLimit: null,
            CasualtyIsLiability: false,
          }
        : null,
    IncludeInGrossEstate: true,
    IsOrion: false,
    OrionManagementStyleId: null,
    HasAdvisorChangedBrokerOfRecord: false,
    OwnerModel: [
      {
        Id: null,
        Name: data.accountOptions.IndividualOwnerOptions.People[0].Name,
        OwnerType: OwnerTypeDTO.Principal,
        Percentage: 1,
      },
    ],
  };
};

interface AccountPortfolioDataToFormParams {
  accountPortfolio?: AccountPortfolioDTO;
  defaultAccountPortfolioOptions: Record<
    ManualAccountsAssetAllocationTaxStatusIdDTO,
    AssetAllocationDTO[]
  >;
}

export const accountPortfolioDataToForm = ({
  accountPortfolio,
  defaultAccountPortfolioOptions,
}: AccountPortfolioDataToFormParams) => {
  const defaultAssetAllocation = getDefaultAssetAllocation(defaultAccountPortfolioOptions, {
    AccountType: AccountType.Insurance,
    TaxStatus: TaxStatusDTO.PreTax,
    AnnuityIsAnnuitized: false,
  });

  const assetAllocation = accountPortfolio?.AssetAllocation || defaultAssetAllocation;

  if (!assetAllocation) {
    return {};
  }

  const assets = getColorByColorGroup(assetAllocation).map(asset => {
    return {
      ...asset,
      y: new Decimal(asset.y).times(100).toNumber(),
    };
  });

  return {
    assets,
    hypotheticalReturn: accountPortfolio?.HypotheticalReturn || '',
  };
};

export const INSURANCE_TYPES_WITH_BENEFICIARIES_TAB = [InsuranceType.Life];

interface GetHasAssetAllocationTabParams {
  insurance: Partial<InsuranceAccountDTO>;
}

export const getShowAssetAllocationTab = ({ insurance }: GetHasAssetAllocationTabParams) => {
  if (
    insurance.InsuranceType === InsuranceType.Life &&
    'LifeInsurancePolicyType' in insurance &&
    insurance.LifeInsurancePolicyType === LifeInsurancePolicyTypeEnum.Permanent &&
    'LifeInsurancePermanentType' in insurance &&
    insurance.LifeInsurancePermanentType === LifeInsurancePermanentTypeEnum.VariableLife
  ) {
    return true;
  }

  return false;
};

export interface FormProps {
  data: InsuranceData;
  insurance: InsuranceToUpsert;
  accountPortfolioData?: AccountPortfolioDTO;
  isEstatePlanningWorkflow: boolean;
  setAlert: (message: string | null) => void;
}

export const validation = (data: InsuranceData) =>
  yup.object().shape({
    accountPortfolio: yup
      .object()
      .shape({})
      .when(['InsuranceType', 'LifeInsurancePolicyType', 'LifeInsurancePermanentType'], {
        is: (insuranceType, lifeInsurancePolicyType, lifeInsurancePermanentType) => {
          return (
            insuranceType === InsuranceType.Life &&
            lifeInsurancePolicyType === LifeInsurancePolicyTypeEnum.Permanent &&
            lifeInsurancePermanentType === LifeInsurancePermanentTypeEnum.VariableLife
          );
        },
        then: yup.object().shape({
          hypotheticalReturn: hypotheticalReturnValidation,
        }),
        otherwise: yup.object().notRequired(),
      }),

    PropertyAndCasualtyInsuranceDetails: yup
      .object()
      .shape({
        PropertyTypes: yup.array().min(1, 'At least one property type is required'),
        Coverage: yup
          .number()
          .typeError('Coverage is required')
          .required('Coverage is required'),
        CoverageStartYear: yup
          .number()
          .typeError('Coverage start year is required')
          .required('Coverage start year is required'),
      })
      .nullable(),
    Premium: yup.mixed().when('InsuranceType', {
      is: InsuranceType.PropertyAndCasualty,
      then: yup
        .number()
        .typeError('Premium is required')
        .required('Premium is required')
        .min(1, 'Premium must be greater than 0'),
    }),

    AccountBeneficiaries: yup
      .object()
      .shape({
        Beneficiaries: yup.array(),
      })
      .test(
        'primary-required-if-contingent',
        'You must have a primary beneficiary if you are adding a contingent beneficiary',
        function(accountBeneficiaries) {
          const { InsuranceType: insType } = this.parent || {};
          if (insType !== InsuranceType.Life) return true;
          const beneficiaries = accountBeneficiaries?.Beneficiaries;
          if (!beneficiaries || beneficiaries.length === 0) return true;
          const hasContingent = beneficiaries.some(
            (b: { Type?: number }) => b.Type === BeneficiaryType.Contingent,
          );
          const hasPrimary = beneficiaries.some(
            (b: { Type?: number }) => b.Type === BeneficiaryType.Primary,
          );
          if (hasContingent && !hasPrimary) return false;
          return true;
        },
      )
      .test(
        'beneficiaries-total-100',
        'The total of beneficiaries percentages added must add up to 100%',
        function(accountBeneficiaries) {
          const { InsuranceType: insType } = (this.parent || {}) as {
            InsuranceType?: InsuranceType;
          };
          if (insType !== InsuranceType.Life) return true;
          const beneficiaries = accountBeneficiaries?.Beneficiaries;
          if (!beneficiaries || beneficiaries.length === 0) return true;
          const primary = beneficiaries.filter(
            (b: { Type?: number }) => b.Type === BeneficiaryType.Primary,
          );
          const contingent = beneficiaries.filter(
            (b: { Type?: number }) => b.Type === BeneficiaryType.Contingent,
          );
          const primarySum = primary.reduce(
            (acc: number, b: { Percentage?: number }) => acc + Number(b.Percentage || 0),
            0,
          );
          const contingentSum = contingent.reduce(
            (acc: number, b: { Percentage?: number }) => acc + Number(b.Percentage || 0),
            0,
          );
          const primaryValid = primary.length === 0 || primarySum === 100;
          const contingentValid = contingent.length === 0 || contingentSum === 100;
          return primaryValid && contingentValid;
        },
      )
      .test(
        'owner-cannot-be-beneficiary',
        'The account owner cannot be added as a primary or contingent beneficiary',
        function(accountBeneficiaries) {
          const parent = (this.parent || {}) as {
            InsuranceType?: InsuranceType;
            OwnerModel?: { Id?: string | null; Name?: string; OwnerType?: OwnerTypeDTO }[];
            LifeInsuredMember?: string | null;
          };
          if (parent.InsuranceType !== InsuranceType.Life) return true;
          const beneficiaries = accountBeneficiaries?.Beneficiaries;
          if (!beneficiaries?.length) return true;

          const ownerModel = parent.OwnerModel;
          const owner = ownerModel?.[0];
          const people = data.Options?.InsuranceOwnerOptions?.People ?? [];
          const insuredName = parent.LifeInsuredMember?.toLowerCase().trim();

          const isLifeInsuranceWithDifferentInsured =
            !!insuredName &&
            !(
              (owner?.OwnerType === OwnerTypeDTO.Principal &&
                people[0]?.Name?.toLowerCase().trim() === insuredName) ||
              (owner?.OwnerType === OwnerTypeDTO.Spouse &&
                people[1]?.Name?.toLowerCase().trim() === insuredName)
            );

          if (isLifeInsuranceWithDifferentInsured) return true;

          const beneficiariesOptions = data.accountOptions?.BeneficiariesOptions ?? [];
          const ownerRows = getBeneficiaryOptionRowsForOwner(owner, beneficiariesOptions);
          if (!ownerRows.length) return true;

          const hasOwnerAsBeneficiary = beneficiaries.some(
            (b: { Id?: string | null; Name?: string }) => beneficiaryRowMatchesOwner(b, ownerRows),
          );
          return !hasOwnerAsBeneficiary;
        },
      )

      .default({ Beneficiaries: [], LastBeneficiaryUpdate: null }),
  });
